import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Users, Plus, Edit, Trash2, Linkedin, ExternalLink, Search, Building2, Phone, Mail } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../config/axios';

const Contacts = () => {
  const [searchParams] = useSearchParams();
  const [contacts, setContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(searchParams.get('company') || '');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  
  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm({
    defaultValues: {
      company_id: '',
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      position: '',
      department: '',
      linkedin_url: '',
      notes: ''
    }
  });

  useEffect(() => {
    fetchCompanies();
    if (selectedCompany) {
      fetchContacts(selectedCompany);
      // Automatically show the form if company is provided in URL
      setShowAddForm(true);
    }
  }, [selectedCompany]);

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/api/companies?limit=100');
      setCompanies(response.data.companies || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast.error('Failed to load companies');
    }
  };

  const fetchContacts = async (companyId) => {
    try {
      setLoading(true);
      const response = await api.get(`/api/contacts/company/${companyId}`);
      setContacts(response.data.contacts || []);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyChange = (companyId) => {
    setSelectedCompany(companyId);
    if (companyId) {
      fetchContacts(companyId);
    } else {
      setContacts([]);
    }
  };

  const onSubmit = async (data) => {
    try {
      // Add company_id to the data
      const contactData = {
        ...data,
        company_id: parseInt(selectedCompany)
      };

      if (editingContact) {
        await api.put(`/api/contacts/${editingContact.id}`, contactData);
        toast.success('Contact updated successfully!');
      } else {
        await api.post('/api/contacts', contactData);
        toast.success('Contact created successfully!');
      }
      
      reset();
      setEditingContact(null);
      setShowAddForm(false);
      fetchContacts(selectedCompany);
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to save contact';
      toast.error(message);
    }
  };

  const handleEdit = (contact) => {
    setEditingContact(contact);
    setValue('first_name', contact.first_name || '');
    setValue('last_name', contact.last_name || '');
    setValue('email', contact.email || '');
    setValue('phone', contact.phone || '');
    setValue('position', contact.position || '');
    setValue('department', contact.department || '');
    setValue('linkedin_url', contact.linkedin_url || '');
    setValue('notes', contact.notes || '');
    setShowAddForm(true);
  };

  const handleDelete = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) {
      return;
    }

    try {
      await api.delete(`/api/contacts/${contactId}`);
      toast.success('Contact deleted successfully!');
      fetchContacts(selectedCompany);
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to delete contact';
      toast.error(message);
    }
  };

  const openLinkedIn = (url) => {
    if (!url) return;
    
    // Ensure URL has protocol
    let formattedUrl = url;
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      formattedUrl = 'https://' + url;
    }
    
    window.open(formattedUrl, '_blank', 'noopener,noreferrer');
  };

  const filteredContacts = contacts.filter(contact => {
    const fullName = `${contact.first_name} ${contact.last_name}`.toLowerCase();
    const position = (contact.position || '').toLowerCase();
    const department = (contact.department || '').toLowerCase();
    const searchLower = searchTerm.toLowerCase();
    
    return fullName.includes(searchLower) || 
           position.includes(searchLower) || 
           department.includes(searchLower);
  });

  return (
    <div className="space-y-6">
      <div className="page-header">
        <h1 className="page-title">Contacts</h1>
        <p className="page-subtitle">Manage your contact relationships and LinkedIn profiles</p>
      </div>

      {/* Company Selection and Search */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="form-label">Select Company</label>
              <select
                value={selectedCompany}
                onChange={(e) => handleCompanyChange(e.target.value)}
                className="form-select"
              >
                <option value="">Choose a company...</option>
                {companies.map(company => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="form-label">Search Contacts</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, position, or department..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-input pl-10"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Contact Form */}
      {selectedCompany && (
        <div className="card">
          <div className="card-header">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                {editingContact ? 'Edit Contact' : 'Add New Contact'}
              </h2>
              <button
                onClick={() => {
                  if (showAddForm) {
                    reset();
                    setShowAddForm(false);
                  } else {
                    reset();
                    setShowAddForm(true);
                  }
                }}
                className="btn btn-outline btn-sm"
              >
                {showAddForm ? 'Cancel' : <Plus className="h-4 w-4 mr-2" />}
                {showAddForm ? 'Cancel' : 'Add Contact'}
              </button>
            </div>
          </div>
          
          {showAddForm && (
            <div className="card-body">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">First Name *</label>
                    <input
                      type="text"
                      {...register('first_name', { required: true })}
                      className="form-input"
                    />
                  </div>
                  
                  <div>
                    <label className="form-label">Last Name *</label>
                    <input
                      type="text"
                      {...register('last_name', { required: true })}
                      className="form-input"
                    />
                  </div>
                  
                  <div>
                    <label className="form-label">Position</label>
                    <input
                      type="text"
                      {...register('position')}
                      className="form-input"
                      placeholder="e.g., Sales Manager, CEO, Developer"
                    />
                  </div>
                  
                  <div>
                    <label className="form-label">Department</label>
                    <input
                      type="text"
                      {...register('department')}
                      className="form-input"
                      placeholder="e.g., Sales, Engineering, Marketing"
                    />
                  </div>
                  
                  <div>
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      {...register('email')}
                      className="form-input"
                      placeholder="contact@company.com"
                    />
                  </div>
                  
                  <div>
                    <label className="form-label">Phone</label>
                    <input
                      type="tel"
                      {...register('phone')}
                      className="form-input"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="form-label">LinkedIn URL</label>
                    <input
                      type="url"
                      {...register('linkedin_url')}
                      className="form-input"
                      placeholder="https://linkedin.com/in/username or linkedin.com/in/username"
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      You can enter just the LinkedIn username or the full URL
                    </p>
                  </div>
                  
                  <div className="md:col-span-2">
                    <label className="form-label">Notes</label>
                    <textarea
                      {...register('notes')}
                      className="form-textarea"
                      rows="3"
                      placeholder="Additional information about this contact..."
                    />
                  </div>
                </div>
                
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      reset();
                      setShowAddForm(false);
                    }}
                    className="btn btn-outline"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                  >
                    {editingContact ? 'Update Contact' : 'Create Contact'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      )}

      {/* Contacts List */}
      {selectedCompany && (
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-semibold text-gray-900">
              Contacts ({filteredContacts.length})
            </h3>
          </div>
          <div className="card-body">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : filteredContacts.length > 0 ? (
              <div className="space-y-4">
                {filteredContacts.map((contact) => (
                  <div key={contact.id} className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="text-lg font-medium text-gray-900">
                            {contact.first_name} {contact.last_name}
                          </h4>
                          {contact.linkedin_url && (
                            <button
                              onClick={() => openLinkedIn(contact.linkedin_url)}
                              className="inline-flex items-center text-blue-600 hover:text-blue-800 transition-colors"
                              title="View LinkedIn Profile"
                            >
                              <Linkedin className="h-4 w-4 mr-1" />
                              <span className="text-sm">LinkedIn</span>
                              <ExternalLink className="h-3 w-3 ml-1" />
                            </button>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
                          {contact.position && (
                            <div className="flex items-center">
                              <Users className="h-4 w-4 mr-2 text-gray-400" />
                              <span className="font-medium">Position:</span>
                              <span className="ml-2">{contact.position}</span>
                            </div>
                          )}
                          {contact.department && (
                            <div className="flex items-center">
                              <Building2 className="h-4 w-4 mr-2 text-blue-600" />
                              <span className="font-medium">Department:</span>
                              <span className="ml-2">{contact.department}</span>
                            </div>
                          )}
                          {contact.email && (
                            <div className="flex items-center">
                              <Mail className="h-4 w-4 mr-2 text-gray-400" />
                              <span className="font-medium">Email:</span>
                              <a 
                                href={`mailto:${contact.email}`}
                                className="ml-2 text-blue-600 hover:text-blue-800"
                              >
                                {contact.email}
                              </a>
                            </div>
                          )}
                          {contact.phone && (
                            <div className="flex items-center">
                              <Phone className="h-4 w-4 mr-2 text-gray-400" />
                              <span className="font-medium">Phone:</span>
                              <a 
                                href={`tel:${contact.phone}`}
                                className="ml-2 text-blue-600 hover:text-blue-800"
                              >
                                {contact.phone}
                              </a>
                            </div>
                          )}
                        </div>
                        
                        {contact.notes && (
                          <div className="mt-3 p-3 bg-white rounded border-l-4 border-blue-200">
                            <p className="text-sm text-gray-700">{contact.notes}</p>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2 ml-4">
                        <button
                          onClick={() => handleEdit(contact)}
                          className="btn btn-outline btn-sm"
                          title="Edit Contact"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(contact.id)}
                          className="btn btn-danger btn-sm"
                          title="Delete Contact"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No contacts found</h3>
                <p className="text-gray-500 mb-4">
                  {searchTerm ? 'Try adjusting your search terms' : 'Get started by adding your first contact'}
                </p>
                {!searchTerm && (
                  <button
                    onClick={() => setShowAddForm(true)}
                    className="btn btn-primary"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Contact
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* No Company Selected */}
      {!selectedCompany && (
        <div className="card">
          <div className="card-body text-center py-12">
                          <Building2 className="h-16 w-16 text-blue-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Company</h3>
            <p className="text-gray-500">Choose a company from the dropdown above to view and manage its contacts.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contacts;
